
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Bug, TestTube } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { shouldShowDebugFeatures } from '@/lib/environment';
import { Link } from 'react-router-dom';

export const DebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, profile } = useAuth();
  const { 
    subscribed, 
    subscriptionTier, 
    subscriptionEnd, 
    loading,
    isTrialActive,
    trialPlan,
    trialEndsAt,
    trialDaysRemaining 
  } = useSubscription();

  if (!shouldShowDebugFeatures()) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center gap-2">
            <Bug className="h-4 w-4" />
            Debug
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-2">
          <Card className="w-80 max-h-96 overflow-auto">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bug className="h-4 w-4" />
                Debug Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs">
              
              {/* Authentication Status */}
              <div>
                <h4 className="font-medium mb-1">Authentication</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <Badge variant={user ? "default" : "destructive"} className="text-xs">
                      {user ? "Authenticated" : "Not Authenticated"}
                    </Badge>
                  </div>
                  {user && (
                    <>
                      <div className="flex justify-between">
                        <span>Email:</span>
                        <span className="truncate max-w-32">{user.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Role:</span>
                        <span>{profile?.role || 'Unknown'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Subscription Status */}
              <div>
                <h4 className="font-medium mb-1">Subscription</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <Badge variant={subscribed ? "default" : "secondary"} className="text-xs">
                      {loading ? "Loading..." : subscribed ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  {subscriptionTier && (
                    <div className="flex justify-between">
                      <span>Tier:</span>
                      <span>{subscriptionTier}</span>
                    </div>
                  )}
                  {subscriptionEnd && (
                    <div className="flex justify-between">
                      <span>Ends:</span>
                      <span>{new Date(subscriptionEnd).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Trial Status */}
              {(isTrialActive || trialPlan) && (
                <div>
                  <h4 className="font-medium mb-1">Trial</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <Badge variant={isTrialActive ? "default" : "secondary"} className="text-xs">
                        {isTrialActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    {trialPlan && (
                      <div className="flex justify-between">
                        <span>Plan:</span>
                        <span>{trialPlan}</span>
                      </div>
                    )}
                    {trialEndsAt && (
                      <div className="flex justify-between">
                        <span>Days Left:</span>
                        <span>{trialDaysRemaining}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Environment */}
              <div>
                <h4 className="font-medium mb-1">Environment</h4>
                <div className="flex justify-between">
                  <span>Mode:</span>
                  <Badge variant="outline" className="text-xs">
                    {window.location.hostname === 'localhost' ? 'Development' : 'Production'}
                  </Badge>
                </div>
              </div>

              {/* Testing Tools */}
              <div className="pt-2 border-t">
                <Link to="/test-payments">
                  <Button size="sm" variant="outline" className="w-full flex items-center gap-2">
                    <TestTube className="h-3 w-3" />
                    Payment Testing
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
